import { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';

interface AgencyFilterProps {
  agencies: string[];
  selectedAgencies: string[];
  onChange: (agencies: string[]) => void;
}

export default function AgencyFilter({ agencies, selectedAgencies, onChange }: AgencyFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCount = selectedAgencies.length;
  const allSelected = selectedCount === agencies.length && agencies.length > 0;
  const hasFilter = selectedCount > 0 && !allSelected;

  const toggleAll = () => {
    onChange(allSelected ? [] : [...agencies]);
  };

  const toggleAgency = (agency: string) => {
    if (selectedAgencies.includes(agency)) {
      onChange(selectedAgencies.filter(a => a !== agency));
    } else {
      onChange([...selectedAgencies, agency]);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40 bg-white ${hasFilter ? 'border-indigo-300 text-slate-800' : 'border-slate-200 text-slate-500'}`}
      >
        <Building2 size={14} className="text-slate-400 shrink-0" />
        <span className="truncate max-w-[160px]">
          {selectedCount === 0 || allSelected ? 'Toutes les agences' :
           `${selectedCount} agence${selectedCount > 1 ? 's' : ''}`}
        </span>
        {hasFilter && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-indigo-600 text-white text-xs font-bold shrink-0">
            {selectedCount}
          </span>
        )}
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-30 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          <div
            className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
            onClick={toggleAll}
          >
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Toutes les agences</span>
            {allSelected && <Check size={14} className="text-indigo-600" />}
          </div>
          {agencies.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-slate-400 italic">Aucune agence</div>
          ) : (
            agencies.map((agency) => {
              const selected = selectedAgencies.includes(agency);
              return (
                <div
                  key={agency}
                  className={`flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors ${selected ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                  onClick={() => toggleAgency(agency)}
                >
                  <span className={`text-sm ${selected ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                    {agency}
                  </span>
                  {selected && <Check size={14} className="text-indigo-600 shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
